'use client'

import { useState } from 'react'
import type { JobsSectionFragment } from '@/lib/types/datocms/graphql/generated'
import { TextWithHighlight } from '../TextWithHighlight'
import Eyebrow from '../ui/Eyebrow'
import Job from '../Job'
import CircleGraphic from '../ui/CircleGraphic'
import Image from 'next/image'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

export default function JobsSection({ title, tag, image, jobs }: Readonly<JobsSectionFragment>) {
	const [activeAccordion, setActiveAccordion] = useState<string | null>(null)

	const handleAccordionClick = (id: string) => {
		if (activeAccordion === id) {
			setActiveAccordion(null)
		} else {
			setActiveAccordion(id)
		}
	}

	return (
		<section className="jobs-section bg-blue-dark @container">
			<div className="relative common-x-padding py-3 sm:py-10 @3xl:py-16 lg:py-32 overflow-hidden">
				<div className="relative bg-white  max-w-screen-2xl mx-auto flex flex-col @3xl:flex-row gap-10 lg:gap-x-44 p-6 @3xl:p-5 rounded-lg z-10">
					<div className="w-full @3xl:w-1/2">
						{tag && <Eyebrow>{tag}</Eyebrow>}
						{title && !isStructuredTextEmpty(title.text) && (
							<div className="heading-size-3 mt-6 2">
								<TextWithHighlight text={title} />
							</div>
						)}
						<div className="mt-16 @3xl:mt-32">
							{jobs.map((job) => (
								<Job
									key={job.id as string}
									{...job}
									activeAccordion={activeAccordion}
									handleAccordionClick={handleAccordionClick}
								/>
							))}
						</div>
					</div>
					<div className="relative w-full @3xl:w-1/2 h-full aspect-square bg-gray-light rounded-md overflow-hidden">
						<Image
							src={image?.[0]?.url as string}
							alt={(image?.[0]?.title as string) ?? 'Jobs Section Image'}
							width={300}
							height={300}
							className="object-cover h-full w-full"
							sizes="(max-width: 768px) 90vw, 40vw"
						/>
					</div>
				</div>

				{/* Background pattern */}
				<div className="absolute -top-[20vh] left-0 h-full w-full grid grid-cols-3 gap-20 scale-125 pointer-events-none">
					{Array.from({ length: 6 }).map((_, i) => (
						<CircleGraphic
							key={`shape-${i + 1}`}
							className="@3xl:h-full @3xl:w-full bg-white [&>div]:bg-contrast aspect-square @3xl:[&>div]:h-3/4 @3xl:[&>div]:w-3/4"
						/>
					))}
				</div>
			</div>
		</section>
	)
}
